import { createContext, useContext } from 'react';
import { PAGES, COMPONENTS } from '@/config/pagePaths.generated.js';

// RBAC for the game settings sections (/game/<slug>/settings/<key>). One route renders every section, so the
// route guard and the Save button look the section up here instead of carrying a page id each.
//
//   page - the RBAC page that must not be NO_ACCESS to open the section (the route guard and the nav).
//   edit - the component that must be FULL_ACCESS to change anything; below it the section is read-only.
//
// Each pair is the gate the section's own backend endpoints enforce, so the UI offers exactly what the server
// will accept. `configs` and `invite` are onboarding steps rather than sidebar pages, but the route accepts
// any key, so they are gated too.
export const SETTINGS_ACCESS = {
    details:    { page: PAGES.GAME_SETUP,       edit: COMPONENTS.GAME_SETUP_MUTATE_GAME_SETTINGS },
    // The SDK package browser: read-only (the release feed itself is public), but a settings page like the
    // rest, so it is hidden from roles without it and can be granted or denied per user.
    sdk:        { page: PAGES.SDK_INTEGRATION,  edit: null },
    versions:   { page: PAGES.CLIENT_VERSIONS,  edit: COMPONENTS.CLIENT_VERSIONS_MUTATE_CLIENT_VERSION },
    systems:    { page: PAGES.GAME_FEATURES,    edit: COMPONENTS.GAME_FEATURES_MUTATE_GAME_FEATURE },
    configs:    { page: PAGES.GAME_CONFIG,      edit: COMPONENTS.GAME_CONFIG_MUTATE_GAME_CONFIG },
    objectives: { page: PAGES.OBJECTIVES,       edit: COMPONENTS.OBJECTIVES_MUTATE_OBJECTIVES },
    history:    { page: PAGES.GAME_HISTORY,     edit: COMPONENTS.GAME_HISTORY_UPLOAD_GAME_HISTORY },
    knowledge:  { page: PAGES.GAME_KNOWLEDGE,   edit: COMPONENTS.GAME_KNOWLEDGE_MUTATE_GAME_KNOWLEDGE },
    data:       { page: PAGES.DATA_CONNECTIONS, edit: COMPONENTS.DATA_CONNECTIONS_MUTATE_CONNECTION },
    invite:     { page: PAGES.USER_MGMT,        edit: COMPONENTS.USER_MGMT_INVITE_USER },
};

/** The RBAC page gating a settings section; unknown keys fall back to Game Setup rather than open. */
export const settingsPagePath = (key) => (key in SETTINGS_ACCESS ? SETTINGS_ACCESS[key].page : PAGES.GAME_SETUP);

/**
 * Whether the section in view is read-only for this user (no edit grant, or the game is frozen). Provided by
 * SettingsPage; the onboarding wizard renders the same bodies without a provider, so there it is false — the
 * wizard is the game's owner setting it up.
 */
export const SettingsReadOnlyContext = createContext(false);
export const useSettingsReadOnly = () => useContext(SettingsReadOnlyContext);
